/**
 * BulkInvoiceModal.jsx — One invoice covering several jobs for the same client.
 *
 * Opened from the Jobs list multi-select. Creates a job-less invoice
 * (job_id NULL) that carries the covered jobs in job_ids and a per-job
 * breakdown snapshot in line_items:
 *   [{ jobId, title, location, amount, services: [{ desc, amount }] }]
 *
 * The snapshot is captured here, at creation time, so the PDF / Xero / email
 * breakdown survives later job edits. Per-job amount is revenue minus any
 * PAID deposit (parity with InvoiceForm's balance math — an unpaid deposit
 * does not reduce what is owed).
 */

import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, Divider, Stack,
} from '@mui/material';
import { uid, fmtC, fmtD, nextInvoiceNumber } from '../../lib/utils';
import { defaultDueDate, termsLabel } from '../../lib/invoiceDefaults';
import { useUpdateJob } from '../../hooks/useJobs';
import { useInvoices, useCreateInvoice } from '../../hooks/useInvoices';
import { useSettings } from '../../hooks/useSettings';
import { useXeroStatus, useSyncInvoiceToXero } from '../../hooks/useXero';
import { useToast } from '../layout/Toast';

const round2 = (n) => Math.round(n * 100) / 100;

function jobPaidDeposits(job, allInvoices) {
  return (allInvoices || [])
    .filter((inv) => inv.jobId === job?.id && inv.invoiceType === 'deposit' && inv.status === 'paid')
    .reduce((s, inv) => s + (parseFloat(inv.amount) || 0), 0);
}

/** Per-job billable amount: revenue minus paid (non-void) deposits. */
export function jobBillableAmount(job, allInvoices) {
  const revenue = parseFloat(job?.revenue) || 0;
  return Math.max(0, round2(revenue - jobPaidDeposits(job, allInvoices)));
}

// Service rows snapshot. calcSnapshot lines are pre-discount/pre-deposit, so
// reconciling rows are appended until the rows sum to the billable amount —
// otherwise the PDF would print services adding up to more than the property
// subtotal.
function jobServices(job, allInvoices, billable) {
  const snap = job?.calcSnapshot;
  const lines = [...(snap?.deliverableLines || []), ...(snap?.addOnLines || [])];
  const services = lines.map((l) => ({
    desc: l.desc || 'Service',
    amount: round2(parseFloat(l.amount) || 0),
  }));
  if (!services.length) return services;
  const gross = round2(services.reduce((s, x) => s + x.amount, 0));
  const paidDeposits = round2(jobPaidDeposits(job, allInvoices));
  const preDeposit = round2(billable + paidDeposits); // = job revenue (net of discount)
  const discount = round2(gross - preDeposit);
  if (Math.abs(discount) > 0.005) {
    const pct = parseFloat(job?.discountPct) || 0;
    services.push({
      desc: discount > 0 ? (pct ? `Discount (${pct}%)` : 'Discount') : 'Adjustment',
      amount: -discount,
    });
  }
  if (paidDeposits > 0.005) {
    services.push({ desc: 'Less deposit paid', amount: -paidDeposits });
  }
  return services;
}

export default function BulkInvoiceModal({ open, jobs = [], onClose, onCreated }) {
  const { toast } = useToast();
  const { data: settings } = useSettings();
  const { data: allInvoices = [], refetch: refetchInvoices } = useInvoices();
  const createMutation = useCreateInvoice();
  const updateJobMutation = useUpdateJob();
  const { data: xeroStatus } = useXeroStatus();
  const syncMutation = useSyncInvoiceToXero();
  const xeroConnected = xeroStatus?.connected === true;

  const [dueDate, setDueDate] = useState(() => defaultDueDate('full', settings));
  const [notes, setNotes] = useState('');

  const client = jobs[0]?.clientName || jobs[0]?.client || '';
  const rows = jobs.map((j) => ({ job: j, amount: jobBillableAmount(j, allInvoices) }));
  const total = Math.round(rows.reduce((s, r) => s + r.amount, 0) * 100) / 100;
  const previewNumber = nextInvoiceNumber(settings?.data, allInvoices);

  const handleCreate = async () => {
    if (!jobs.length) return;
    // Mint the number off a server-fresh list — the render-time cache can be
    // stale and the DB has a UNIQUE constraint on number (SendQuoteModal
    // pattern).
    let fresh = allInvoices;
    try {
      const r = await refetchInvoices();
      if (r.data) fresh = r.data;
    } catch { /* fall back to cache */ }

    const invoice = {
      id: uid(),
      number: nextInvoiceNumber(settings?.data, fresh),
      jobId: null,
      jobIds: jobs.map((j) => j.id),
      contactId: jobs[0]?.contactId || null,
      client,
      invoiceType: 'full',
      amount: total,
      paidAmount: 0,
      status: 'unpaid',
      terms: termsLabel('full', settings),
      dueDate,
      notes: notes.trim() || null,
      payments: [],
      lineItems: rows.map(({ job, amount }) => ({
        jobId: job.id,
        title: job.title || '',
        location: job.location || '',
        amount,
        services: jobServices(job, fresh, amount),
      })),
    };

    try {
      await createMutation.mutateAsync(invoice);
      toast(`Invoice ${invoice.number} created — ${fmtC(total)} across ${jobs.length} jobs`);
      if (xeroConnected) syncMutation.mutate(invoice.id);
      // The invoice exists, so the covered jobs ARE invoiced — flip them now
      // rather than waiting for the send (Cyprian 2026-08-14).
      for (const j of jobs) {
        if (!['delivered', 'partial_delivery'].includes(j.status)) continue;
        try {
          await updateJobMutation.mutateAsync({ id: j.id, status: 'invoiced' });
        } catch (err) {
          console.error('Bulk invoice: job status flip failed', j.id, err);
        }
      }
      onCreated?.(invoice);
    } catch (err) {
      toast(`Failed to create invoice: ${err.message}`, 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
          <span>Bulk invoice — {client || 'Client'}</span>
          <Typography component="span" sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
            {previewNumber}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack divider={<Divider flexItem />} spacing={1}>
          {rows.map(({ job, amount }) => (
            <Box key={job.id} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {job.location || job.title || '(Untitled)'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.66rem' }}>
                  {fmtD(job.shootDate) || 'No shoot date'}
                  {amount !== (parseFloat(job.revenue) || 0) ? ` · ${fmtC(job.revenue)} less paid deposit` : ''}
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.85rem', flexShrink: 0 }}>
                {fmtC(amount)}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 2, pt: 1.5, borderTop: 2, borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
          </Typography>
          <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.5rem' }}>
            {fmtC(total)}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <TextField
            label="Due date"
            type="date"
            size="small"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <TextField
            label="Notes"
            size="small"
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={createMutation.isPending || !jobs.length || total <= 0}
          sx={{ textTransform: 'none' }}
        >
          {createMutation.isPending ? 'Creating…' : `Create invoice · ${fmtC(total)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
